import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtIDR, fmtDate } from "@/lib_app/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, CheckCircle2, AlertCircle, Clock, Trash2, Paperclip, CheckSquare, MoreVertical, ArrowUp, ArrowDown, ArrowUpDown, LayoutGrid, Rows3 } from "lucide-react";
import Avatar from "@/components/app/Avatar";
import ProjectDetailModal from "@/components/app/ProjectDetailModal";
import Pagination, { paginate } from "@/components/app/Pagination";
import { useAuth } from "@/context/AuthContext";

const MONTH_LBL = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
function fmtMonthKey(k) {
  if (!k || k.length < 7) return "-";
  const [y, m] = k.split("-");
  return `${MONTH_LBL[Number(m) - 1] || m} '${y.slice(2)}`;
}
const projectMonthKey = (r) => (r.deal_date || r.created_at || "").slice(0, 7);

export default function Projects() {
  const { user } = useAuth();
  const canDelete = user && (user.role === "owner" || user.role === "sales_director");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [salesFilter, setSalesFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [selectedTab, setSelectedTab] = useState("info");
  const openAttachments = (r) => { setSelected(r); setSelectedTab("lampiran"); };
  const [deletingId, setDeletingId] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [kebabOpen, setKebabOpen] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const cycleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortBy(null); setSortDir(null);
  };

  const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };
  const toggleOne = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAllVisible = (visible) => setSelectedIds((s) => {
    const allSelected = visible.length > 0 && visible.every((r) => s.has(r.id));
    if (allSelected) {
      return new Set([...s].filter((id) => !visible.some((r) => r.id === id)));
    }
    const n = new Set(s);
    visible.forEach((r) => n.add(r.id));
    return n;
  });

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!confirm(`Hapus ${ids.length} proyek terpilih?\n\n📤 Prospek terkait akan otomatis dibuka kembali. Semua pembayaran akan ikut terhapus.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/projects/bulk-delete", { project_ids: ids });
      toast.success(`${r.data.deleted_count} proyek dihapus · ${r.data.reopened_count} prospek dibuka kembali`);
      exitSelectionMode();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBulkBusy(false); }
  };

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("sp_projects_page_size");
    return saved || "25";
  });
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("sp_projects_view") || "table");
  useEffect(() => { localStorage.setItem("sp_projects_page_size", pageSize); }, [pageSize]);
  useEffect(() => { localStorage.setItem("sp_projects_view", viewMode); }, [viewMode]);

  const load = async () => {
    setBusy(true);
    try {
      const [pr, us] = await Promise.all([api.get("/projects"), api.get("/users")]);
      setRows(pr.data || []);
      setUsers(us.data || []);
    } catch (e) {
      toast.error("Gagal memuat proyek");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-open project modal via ?open={project_id}
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    (async () => {
      try {
        const r = await api.get(`/projects/${openId}`);
        setSelected(r.data);
        setStatusFilter("all");
        setSalesFilter("all");
        setMonthFilter("all");
        setSearch("");
      } catch {
        toast.error("Proyek tidak ditemukan");
      }
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    })();
  }, [searchParams, setSearchParams]);

  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (r) => r.deadline && r.deadline < today && r.status !== "done";

  const monthOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { const mk = projectMonthKey(r); if (mk) set.add(mk); });
    return Array.from(set).sort().reverse();
  }, [rows]);

  const filtered = useMemo(() => {
    let arr = rows.filter((r) => {
      if (salesFilter !== "all" && r.sales_id !== salesFilter) return false;
      if (monthFilter !== "all" && projectMonthKey(r) !== monthFilter) return false;
      if (statusFilter === "done" && r.status !== "done") return false;
      if (statusFilter === "on_progress" && r.status !== "on_progress") return false;
      if (statusFilter === "overdue" && !isOverdue(r)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(r.customer || "").toLowerCase().includes(q) && !(r.service || "").toLowerCase().includes(q) && !(r.name || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
    if (sortBy && sortDir) {
      const acc = {
        customer: (r) => (r.customer || "").toLowerCase(),
        sales: (r) => (r.sales_nickname || "").toLowerCase(),
        base: (r) => r.base_value || 0,
        ppn: (r) => r.ppn_amount || 0,
        total: (r) => r.total_value || 0,
        paid: (r) => r.total_paid || 0,
        outstanding: (r) => r.outstanding || 0,
        deadline: (r) => r.deadline || "",
        deal: (r) => r.deal_date || "",
        status: (r) => r.status || "",
      };
      arr = [...arr].sort((a, b) => {
        const va = acc[sortBy]?.(a);
        const vb = acc[sortBy]?.(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
        return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
      });
    }
    return arr;
  }, [rows, salesFilter, monthFilter, statusFilter, search, sortBy, sortDir]);

  const counts = useMemo(() => ({
    all: rows.length,
    on_progress: rows.filter((r) => r.status === "on_progress").length,
    done: rows.filter((r) => r.status === "done").length,
    overdue: rows.filter((r) => isOverdue(r)).length,
  }), [rows]);

  // Summary totals — always follows the currently filtered rows (tab + search)
  const summary = useMemo(() => {
    const s = filtered.reduce((a, r) => {
      a.base += r.base_value || 0;
      a.ppn += r.ppn_amount || 0;
      a.total += r.total_value || 0;
      a.paid += r.total_paid || 0;
      a.outstanding += r.outstanding || 0;
      return a;
    }, { base: 0, ppn: 0, total: 0, paid: 0, outstanding: 0 });
    s.pct = s.total > 0 ? (s.paid * 100) / s.total : 0;
    s.count = filtered.length;
    return s;
  }, [filtered]);

  useEffect(() => { setPage(1); }, [salesFilter, monthFilter, statusFilter, search, pageSize]);
  const { paged, totalPages, curPage, startIdx, endIdx } = paginate(filtered, pageSize, page);

  const removeProject = async (r, e) => {
    e?.stopPropagation();
    if (!canDelete) return;
    const paid = r.total_paid || 0;
    const msg = paid > 0
      ? `Hapus proyek "${r.customer} — ${r.service}"?\n\n⚠ ${fmtIDR(paid)} pembayaran akan ikut terhapus.\n📤 Prospek terkait akan otomatis dibuka kembali (kembali ke daftar Aktif).`
      : `Hapus proyek "${r.customer} — ${r.service}"?\n\n📤 Prospek terkait akan otomatis dibuka kembali (kembali ke daftar Aktif).`;
    if (!confirm(msg)) return;
    setDeletingId(r.id);
    try {
      await api.delete(`/projects/${r.id}`);
      toast.success("Proyek dihapus");
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Gagal hapus proyek");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6 pb-16" data-testid="projects-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Proyek</p>
        <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Daftar Proyek</h1>
        <p className="text-sm text-[#A0A3A8] mt-1">Deal yang sudah menjadi proyek — pantau deadline, status & pembayaran.</p>
      </div>

      {/* Summary — figures follow the active filter (tab + search) */}
      {!busy && rows.length > 0 && (
        <div className="bg-white rounded-[22px] p-5 md:p-6 card-soft" data-testid="project-summary">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-sm font-bold text-[#090808]">Ringkasan Proyek</h2>
            <span className="text-xs text-[#A0A3A8] font-semibold" data-testid="project-summary-count">Berdasarkan {summary.count} proyek terfilter</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="rounded-2xl p-4 bg-[#090808] text-white">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#B7BAC2]">Total Dasar + PPN</div>
              <div className="text-xl md:text-2xl font-extrabold mt-2 font-num tabular-nums break-words" data-testid="summary-total">{fmtIDR(summary.total)}</div>
              <div className="text-[11px] text-[#8A8D96] mt-1.5 font-semibold">Nilai kontrak keseluruhan</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Total Nilai Dasar</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#090808] break-words" data-testid="summary-base">{fmtIDR(summary.base)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Sebelum PPN</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Total PPN</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#090808] break-words" data-testid="summary-ppn">{fmtIDR(summary.ppn)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Total PPN</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#7FBF9B]">Total Terbayar</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#2FA968] break-words" data-testid="summary-paid">{fmtIDR(summary.paid)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Dari pembayaran masuk</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#C9A46A]">Outstanding</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#D98A2B] break-words" data-testid="summary-outstanding">{fmtIDR(summary.outstanding)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Belum tertagih</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#F0F1F4] flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="text-xs text-[#626061] font-semibold whitespace-nowrap">Progres pembayaran: <b className="text-[#090808]">{fmtIDR(summary.paid)}</b> / {fmtIDR(summary.total)}</div>
            <div className="flex-1 min-w-[140px] h-2.5 bg-[#EEF0F4] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#57D28A] to-[#2FA968] rounded-full transition-all" style={{ width: `${Math.min(100, summary.pct)}%` }} />
            </div>
            <div className="text-sm font-extrabold text-[#2FA968] tabular-nums">{summary.pct.toFixed(1).replace(".", ",")}%</div>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectionMode && selectedIds.size > 0 && canDelete && (
        <div className="flex items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-[#090808] text-white shadow-[0_10px_30px_rgba(24,27,51,0.20)]" data-testid="project-bulk-bar">
          <div className="flex items-center gap-3 text-sm">
            <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-white text-[#090808] text-[11px] font-bold">{selectedIds.size}</span>
            <span>proyek dipilih</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exitSelectionMode} data-testid="project-bulk-clear" className="text-xs text-white/70 hover:text-white px-3 h-8 rounded-md">Batal</button>
            <button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="project-bulk-delete-btn"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#FF6B7D] hover:bg-rose-600 text-white text-sm font-semibold disabled:opacity-60"
            >
              <Trash2 className="w-4 h-4" /> {bulkBusy ? "Menghapus..." : `Hapus ${selectedIds.size} Proyek`}
            </button>
          </div>
        </div>
      )}
      {selectionMode && selectedIds.size === 0 && canDelete && (
        <div data-testid="project-selection-hint" className="flex items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-[#E5EEFA] border border-[#B0C7EA]">
          <div className="text-xs text-[#090808] font-semibold flex items-center gap-2">
            <CheckSquare className="w-4 h-4" /> Mode pilih aktif — centang proyek yang ingin dihapus
          </div>
          <button onClick={exitSelectionMode} className="text-[11px] text-[#626061] hover:text-[#090808] font-medium px-2 h-7 rounded-md">Keluar</button>
        </div>
      )}

      {/* Category tabs (Semua / On Progress / Done / Overdue) + View toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all", label: "Semua", count: counts.all, color: "bg-[#090808] text-white" },
            { key: "on_progress", label: "On Progress", count: counts.on_progress, color: "bg-[#3B4A63] text-white" },
            { key: "done", label: "Done", count: counts.done, color: "bg-[#57D28A] text-white" },
            { key: "overdue", label: "Overdue", count: counts.overdue, color: "bg-[#CE6968] text-white" },
          ].map((t) => {
            const active = statusFilter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                data-testid={`project-status-${t.key}`}
                className={`inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold transition-all ${active ? `${t.color} shadow-[0_4px_12px_rgba(0,0,0,0.10)]` : "bg-white text-[#626061] hover:bg-[#F4F5F7] card-soft"}`}
              >
                {t.label}
                <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-[#F4F5F7] text-[#626061]"}`}>{t.count}</span>
              </button>
            );
          })}
        </div>
        <div className="inline-flex bg-white card-soft rounded-full p-1 gap-1">
          <button
            data-testid="project-view-table"
            onClick={() => setViewMode("table")}
            className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
              viewMode === "table" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
            }`}
            title="Tampilan Tabel"
          >
            <Rows3 className="w-3.5 h-3.5" /> Tabel
          </button>
          <button
            data-testid="project-view-card"
            onClick={() => setViewMode("card")}
            className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
              viewMode === "card" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
            }`}
            title="Tampilan Kartu"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Kartu
          </button>
        </div>
      </div>

      {/* Filters row: Search + Tim + Bulan */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
          <Input
            data-testid="project-search"
            placeholder="Cari customer/service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 rounded-full bg-white border-transparent card-soft focus-visible:ring-[#090808]/40"
          />
        </div>
        <Select value={salesFilter} onValueChange={setSalesFilter}>
          <SelectTrigger data-testid="project-sales-filter" className="w-52 h-12 rounded-full bg-white card-soft border-transparent px-5"><SelectValue placeholder="Tim" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tim</SelectItem>
            {users.filter((u) => u.is_active !== false && u.role !== "owner" && u.role !== "finance").map((u) => (
              <SelectItem key={u.user_id} value={u.user_id}>
                <div className="flex items-center gap-2">
                  <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                  <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger data-testid="project-month-filter" className="w-44 h-12 rounded-full bg-white card-soft border-transparent px-5"><SelectValue placeholder="Bulan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Bulan</SelectItem>
            {monthOptions.map((mk) => (
              <SelectItem key={mk} value={mk}>{fmtMonthKey(mk)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canDelete && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setKebabOpen((v) => !v)}
              onBlur={() => setTimeout(() => setKebabOpen(false), 200)}
              data-testid="project-kebab-btn"
              className={`w-12 h-12 rounded-full inline-flex items-center justify-center transition-colors ${selectionMode ? "bg-[#F4F5F7] text-[#090808] card-soft" : "text-[#626061] hover:text-[#090808] hover:bg-[#F4F5F7] card-soft bg-white"}`}
              title="Menu"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {kebabOpen && (
              <div data-testid="project-kebab-menu" className="absolute right-0 top-14 z-30 w-56 rounded-2xl bg-white card-soft border border-[#DCE0E7] py-1.5 overflow-hidden">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setKebabOpen(false); if (selectionMode) exitSelectionMode(); else setSelectionMode(true); }}
                  data-testid="project-toggle-selection-mode"
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#090808] hover:bg-[#F4F5F7] transition-colors"
                >
                  <CheckSquare className="w-4 h-4 text-[#090808]" />
                  <span className="font-medium">{selectionMode ? "Keluar mode pilih" : "Aktifkan mode pilih"}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {busy && <div className="text-sm text-[#A0A3A8]">Memuat proyek...</div>}

      {!busy && filtered.length === 0 && (
        <div className="bg-white rounded-2xl py-16 text-center text-sm text-[#A0A3A8] card-soft">
          Belum ada proyek. Tandai prospek sebagai <span className="font-semibold text-[#090808]">Deal</span> untuk otomatis membuat proyek.
        </div>
      )}

      {!busy && filtered.length > 0 && viewMode === "table" && (
        <div className="bg-white rounded-2xl card-soft overflow-hidden" data-testid="projects-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px]">
            <thead className="bg-[#F4F5F7]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#626061] font-bold">
                {selectionMode && canDelete && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="project-select-all"
                      checked={paged.length > 0 && paged.every((r) => selectedIds.has(r.id))}
                      ref={(el) => { if (el) el.indeterminate = paged.some((r) => selectedIds.has(r.id)) && !paged.every((r) => selectedIds.has(r.id)); }}
                      onChange={() => toggleAllVisible(paged)}
                      className="w-4 h-4 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-5 py-3"><SortTh label="Customer / Service" sortKey="customer" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                <th className="px-5 py-3"><SortTh label="Tim" sortKey="sales" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                <th className="px-5 py-3 text-right"><SortTh label="Nilai Dasar" sortKey="base" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                <th className="px-5 py-3 text-right"><SortTh label="PPN" sortKey="ppn" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                <th className="px-5 py-3 text-right"><SortTh label="Total" sortKey="total" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                <th className="px-5 py-3 text-right"><SortTh label="Terbayar" sortKey="paid" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                <th className="px-5 py-3 text-right"><SortTh label="Outstanding" sortKey="outstanding" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                <th className="px-5 py-3"><SortTh label="Tanggal" sortKey="deal" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                <th className="px-5 py-3"><SortTh label="Status" sortKey="status" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((r) => {
                const overdue = isOverdue(r);
                const pct = r.total_value > 0 ? (r.total_paid || 0) * 100 / r.total_value : 0;
                const statusChip = r.status === "done"
                  ? { label: "Done", cls: "bg-[#E4F9EC] text-[#4A6D53] border-[#B7E7C7]", icon: <CheckCircle2 className="w-3 h-3" /> }
                  : overdue
                    ? { label: "Overdue", cls: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]", icon: <AlertCircle className="w-3 h-3" /> }
                    : { label: "On Progress", cls: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]", icon: <Clock className="w-3 h-3" /> };
                return (
                  <tr
                    key={r.id}
                    data-testid={`project-row-${r.id}`}
                    onClick={() => (selectionMode ? toggleOne(r.id) : setSelected(r))}
                    className={`border-t border-[#F4F5F7] hover:bg-[#FAFBFD] cursor-pointer transition-colors ${selectionMode && selectedIds.has(r.id) ? "bg-[#F4F5F7]/60" : ""}`}
                  >
                    {selectionMode && canDelete && (
                      <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleOne(r.id)}
                          data-testid={`project-select-${r.id}`}
                          className="w-4 h-4 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-bold text-[#090808]">{r.customer}</div>
                        {((r.prospect_attachments?.length || 0) + (r.quotations_count || 0) + (r.invoice_attachments_count || 0) + (r.po_count || 0) + (r.po_attachments_count || 0)) > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openAttachments(r); }}
                            title={`${r.prospect_attachments?.length || 0} lampiran manual · ${r.quotations_count || 0} quotation · ${r.invoice_attachments_count || 0} dokumen invoice · ${r.po_count || 0} PO · ${r.po_attachments_count || 0} dokumen PO`}
                            data-testid={`project-att-count-${r.id}`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#3B4A63] bg-[#E5EEFA] hover:bg-[#D6E4F8] rounded-full px-1.5 h-4 transition-colors"
                          >
                            <Paperclip className="w-2.5 h-2.5" /> {(r.prospect_attachments?.length || 0) + (r.quotations_count || 0) + (r.invoice_attachments_count || 0) + (r.po_count || 0) + (r.po_attachments_count || 0)}
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-[#626061]">{r.service}{r.contract_no ? ` · ${r.contract_no}` : ""}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Avatar name={r.sales_name} picture={r.sales_picture} size="sm" className="!w-6 !h-6 !text-[10px]" />
                        <span className="text-xs font-semibold text-[#090808]">{r.sales_nickname || "-"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <div className="text-sm font-num text-[#090808] tabular-nums font-semibold">{fmtIDR(r.base_value || 0)}</div>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      {r.ppn_type === "ppn" ? (
                        <div className="text-sm font-num text-[#3B4A63] tabular-nums font-semibold">{fmtIDR(r.ppn_amount || 0)}</div>
                      ) : (
                        <div className="text-xs text-[#A0A3A8] font-semibold">Non-PPN</div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <div className="text-sm font-num font-bold text-[#090808] tabular-nums">{fmtIDR(r.total_value)}</div>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <div className="text-sm font-num text-[#57D28A] font-semibold tabular-nums">{fmtIDR(r.total_paid || 0)}</div>
                      <div className="w-24 h-1.5 rounded-full bg-[#F4F5F7] mt-1 overflow-hidden ml-auto"><div className="h-full bg-[#57D28A]" style={{ width: `${Math.min(100, pct)}%` }} /></div>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap"><div className="text-sm font-num font-semibold text-[#CE6968] tabular-nums">{fmtIDR(r.outstanding || 0)}</div></td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deal</span>
                        <span className="text-xs tabular-nums text-[#3B4A63] font-semibold">{r.deal_date ? fmtDate(r.deal_date) : "-"}</span>
                      </div>
                      <div className="flex items-baseline gap-1.5 mt-0.5">
                        <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deadline</span>
                        <span className={`text-xs tabular-nums font-semibold ${overdue ? "text-[#CE6968]" : "text-[#626061]"}`}>{r.deadline ? fmtDate(r.deadline) : "-"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${statusChip.cls}`}>
                        {statusChip.icon} {statusChip.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!busy && filtered.length > 0 && viewMode === "card" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-card-grid">
          {paged.map((r) => (
            <ProjectCard
              key={r.id}
              row={r}
              overdue={isOverdue(r)}
              selectionMode={selectionMode && canDelete}
              selected={selectedIds.has(r.id)}
              onToggleSelect={() => toggleOne(r.id)}
              onOpen={() => (selectionMode ? toggleOne(r.id) : setSelected(r))}
              onOpenAttachments={() => openAttachments(r)}
            />
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <Pagination
          total={filtered.length}
          page={curPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onPageChange={setPage}
          startIdx={startIdx}
          endIdx={endIdx}
          label="proyek"
          testidPrefix="project-pagination"
        />
      )}

      <ProjectDetailModal open={!!selected} project={selected} users={users} initialTab={selectedTab} onClose={() => { setSelected(null); setSelectedTab("info"); }} onChanged={load} />
    </div>
  );
}

function SortGlyph({ dir }) {
  const upOp = dir === "desc" ? 0.25 : dir === "asc" ? 1 : 0.55;
  const downOp = dir === "asc" ? 0.25 : dir === "desc" ? 1 : 0.55;
  return (
    <svg width="8" height="11" viewBox="0 0 8 11" aria-hidden="true" className="shrink-0">
      <path d="M4 0.5L7.5 4.2H0.5L4 0.5Z" fill="currentColor" opacity={upOp} />
      <path d="M4 10.5L0.5 6.8H7.5L4 10.5Z" fill="currentColor" opacity={downOp} />
    </svg>
  );
}

function SortTh({ label, sortKey, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === sortKey;
  const dir = active ? sortDir : null;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      data-testid={`project-sort-${sortKey}`}
      className={`inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#090808] transition-colors ${active ? "text-[#090808]" : ""}`}
      style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
    >
      {align === "right" && <SortGlyph dir={dir} />}
      <span>{label}</span>
      {align !== "right" && <SortGlyph dir={dir} />}
    </button>
  );
}

function ProjectCard({ row: r, overdue, selectionMode, selected, onToggleSelect, onOpen, onOpenAttachments }) {
  const pct = r.total_value > 0 ? (r.total_paid || 0) * 100 / r.total_value : 0;
  const statusChip = r.status === "done"
    ? { label: "Done", cls: "bg-[#E4F9EC] text-[#4A6D53] border-[#B7E7C7]", icon: <CheckCircle2 className="w-3 h-3" /> }
    : overdue
      ? { label: "Overdue", cls: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]", icon: <AlertCircle className="w-3 h-3" /> }
      : { label: "On Progress", cls: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]", icon: <Clock className="w-3 h-3" /> };
  // Glassy tint by status
  const tint = r.status === "done"
    ? { bg: "linear-gradient(160deg, rgba(180,240,205,0.42) 0%, rgba(200,246,220,0.28) 100%)", border: "rgba(120,200,150,0.35)" }
    : overdue
      ? { bg: "linear-gradient(160deg, rgba(255,193,203,0.42) 0%, rgba(255,213,220,0.28) 100%)", border: "rgba(255,140,155,0.35)" }
      : { bg: "linear-gradient(160deg, rgba(190,210,240,0.42) 0%, rgba(210,222,242,0.28) 100%)", border: "rgba(150,175,220,0.35)" };
  return (
    <div
      data-testid={`project-card-${r.id}`}
      onClick={onOpen}
      className={`group relative rounded-2xl cursor-pointer hover:shadow-[0_10px_28px_rgba(24,27,51,0.14),0_24px_60px_rgba(24,27,51,0.10)] transition-shadow ${selectionMode && selected ? "ring-2 ring-[#090808]/60" : ""}`}
      style={{
        background: tint.bg,
        backdropFilter: "blur(24px) saturate(160%)",
        WebkitBackdropFilter: "blur(24px) saturate(160%)",
        border: `1px solid ${tint.border}`,
        boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
      }}
    >
      {selectionMode && (
        <div className="absolute left-3 top-3 z-10" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            data-testid={`project-card-select-${r.id}`}
            checked={selected}
            onChange={onToggleSelect}
            className="w-5 h-5 rounded border-[#A0A3A8] accent-[#090808] cursor-pointer bg-white"
          />
        </div>
      )}
      <div className={`px-5 pt-5 pb-4 ${selectionMode ? "pl-12" : ""}`}>
        {/* Header: customer + status chip */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-base font-bold text-[#090808] leading-tight truncate">{r.customer}</div>
              {((r.prospect_attachments?.length || 0) + (r.quotations_count || 0) + (r.invoice_attachments_count || 0) + (r.po_count || 0) + (r.po_attachments_count || 0)) > 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenAttachments?.(); }}
                  title={`${r.prospect_attachments?.length || 0} lampiran manual · ${r.quotations_count || 0} quotation · ${r.invoice_attachments_count || 0} dokumen invoice · ${r.po_count || 0} PO · ${r.po_attachments_count || 0} dokumen PO`}
                  data-testid={`project-card-att-count-${r.id}`}
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#3B4A63] bg-white/70 hover:bg-white rounded-full px-1.5 h-4 shrink-0 transition-colors"
                >
                  <Paperclip className="w-2.5 h-2.5" /> {(r.prospect_attachments?.length || 0) + (r.quotations_count || 0) + (r.invoice_attachments_count || 0) + (r.po_count || 0) + (r.po_attachments_count || 0)}
                </button>
              )}
            </div>
            <div className="text-xs text-[#626061] mt-0.5 truncate">{r.service}{r.contract_no ? ` · ${r.contract_no}` : ""}</div>
          </div>
          <span className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold border whitespace-nowrap shrink-0 ${statusChip.cls}`}>
            {statusChip.icon} {statusChip.label}
          </span>
        </div>

        {/* Sales row */}
        <div className="mt-3 flex items-center gap-2">
          <Avatar name={r.sales_name} picture={r.sales_picture} size="sm" className="!w-6 !h-6 !text-[10px]" />
          <span className="text-xs font-semibold text-[#090808]">{r.sales_nickname || "-"}</span>
        </div>

        {/* Financial grid */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#626061] font-semibold">Total</div>
            <div className="font-num text-sm font-bold text-[#090808] tabular-nums leading-tight mt-0.5">{fmtIDR(r.total_value)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#4A6D53] font-semibold">Terbayar</div>
            <div className="font-num text-sm font-bold text-[#57D28A] tabular-nums leading-tight mt-0.5">{fmtIDR(r.total_paid || 0)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#CE6968] font-semibold">Outstanding</div>
            <div className="font-num text-sm font-bold text-[#CE6968] tabular-nums leading-tight mt-0.5">{fmtIDR(r.outstanding || 0)}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-2.5 h-1.5 rounded-full bg-white/60 overflow-hidden">
          <div className="h-full bg-[#57D28A]" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>

        {/* Footer: deal & deadline */}
        <div className="mt-3 flex items-center justify-between text-[11px]">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deal</span>
            <span className="tabular-nums text-[#3B4A63] font-semibold">{r.deal_date ? fmtDate(r.deal_date) : "-"}</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deadline</span>
            <span className={`tabular-nums font-semibold ${overdue ? "text-[#CE6968]" : "text-[#626061]"}`}>{r.deadline ? fmtDate(r.deadline) : "-"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

